import axios, { InternalAxiosRequestConfig } from "axios";
import { config } from "../constants/config";
import { ServiceType } from "../types/api";
import { authManager } from "./authManager";
import { sendPostMessage } from "./postMessage";
import { Event } from "../types/event.types";

interface CustomRequestConfig extends InternalAxiosRequestConfig {
  service?: ServiceType | string;
}

const instance = axios.create({
  timeout: config.http.timeout
});

export default instance;

instance.interceptors.request.use(
  async (req: CustomRequestConfig) => {
    const { service, url } = req;

    if (!url) {
      return Promise.reject(new Error("Request URL is required"));
    }

    req.baseURL = config.api.baseURL;

    // Set headers using Axios header methods
    req.headers.set("Access-Control-Allow-Origin", config.api.allowedOrigin);
    req.headers.set("Access-Control-Allow-Methods", config.http.corsMethods);
    req.headers.set("Access-Control-Allow-Headers", config.http.corsHeaders);
    req.headers.set("X-Device-Id", config.http.deviceId);

    const servicePath = getServicePath(service);
    if (servicePath?.includes("price")) {
      req.headers.set("X-Price-Client-Token", config.client.priceClientToken);
    }

    // Merge auth headers
    const authHeaders = authManager.getAuthHeaders();
    Object.entries(authHeaders).forEach(([key, value]) => {
      if (value) {
        req.headers.set(key, value);
      }
    });

    req.url = servicePath + url;

    return req;
  },
  error => Promise.reject(error)
);

// Handle token expiration
instance.interceptors.response.use(
  response => response,
  error => {
    if (error?.response?.status === 401) {
      sendPostMessage({
        status: Event.TOKEN_EXPIRED,
        message: "Authentication token has expired"
      });
    }
    return Promise.reject(error);
  }
);

/**
 * Maps service type to service path
 */
const getServicePath = (service?: ServiceType | string): string => {
  if (service === ServiceType.WALLET) {
    return config.services.wallet;
  }
  return config.services.backtest;
};
